import React, { useState, useEffect, useCallback, useMemo } from 'react';
import * as XLSX from 'sheetjs-style';
import { apiFetch } from '../api/api';
import { DataForm } from '../components/forms';
import ContextMenu from '../components/ContextMenu';
import './WellBHA.css';

const WellBHA = () => {
    // Core state
    const [wells, setWells] = useState([]);
    const [selectedWellId, setSelectedWellId] = useState('');
    const [bhaData, setBhaData] = useState([]);
    const [bhaComponentData, setBhaComponentData] = useState([]);
    const [selectedBHA, setSelectedBHA] = useState(null);
    
    // Column configurations
    const [bhaColumns, setBhaColumns] = useState([]);
    const [componentColumns, setComponentColumns] = useState([]);
    
    // UI state
    const [isLoading, setIsLoading] = useState(false);
    const [toast, setToast] = useState('');
    const [contextMenu, setContextMenu] = useState({ visible: false, x: 0, y: 0, type: '', record: null });
    
    // Modal/Form state
    const [showBhaForm, setShowBhaForm] = useState(false);
    const [editingBhaId, setEditingBhaId] = useState(null);
    const [showBhaConfig, setShowBhaConfig] = useState(false);
    
    // Component editing state
    const [editingComponent, setEditingComponent] = useState(null);
    const [showComponentForm, setShowComponentForm] = useState(false);
    
    // Image/Attachment state
    const [bhaImage, setBhaImage] = useState(null);
    const [showImagePreview, setShowImagePreview] = useState(false);
    const [isDragOver, setIsDragOver] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    
    // Full screen component view state
    const [showFullScreenComponents, setShowFullScreenComponents] = useState(false);
    
    // New state for summary and tabs
    const [bhaSummary, setBhaSummary] = useState({ totalLength: 0, totalWeight: 0, maxOD: 0, totalComponents: 0 });
    const [activeTab, setActiveTab] = useState('components'); // 'components', 'image'
    const [statistics, setStatistics] = useState({
        totalBHA: 0,
        totalComponents: 0,
        maxLength: 0,
        maxWeight: 0,
        maxOD: 0
    });

    // Configuration state for display fields - now loaded from column configuration
    const [bhaDisplayConfig, setBhaDisplayConfig] = useState([]);
    const [componentDisplayConfig, setComponentDisplayConfig] = useState([]);
    
    const role = localStorage.getItem('role') || 'admin';
    
    // Helper function to convert column configuration to display configuration
    const convertColumnConfigToDisplayConfig = useCallback((columnConfig, tableName) => {
        if (!columnConfig || !Array.isArray(columnConfig.columns)) {
            return [];
        }

        const result = columnConfig.columns
            .filter(col => {
                // Exclude identity columns
                const fieldName = col.name?.toLowerCase() || '';
                const isIdentityColumn = fieldName === 'bhaid' || 
                                       fieldName === 'wellid' || 
                                       fieldName === 'componentid';
                return !isIdentityColumn;
            })
            .map(col => {
                // Determine field type based on column type
                let fieldType = 'text'; // default
                const colType = col.type?.toLowerCase() || '';
                const colName = col.name?.toLowerCase() || '';
                
                if (colType.includes('int') || colType.includes('float') || colType.includes('decimal') || colType.includes('numeric') || colType === 'number') {
                    fieldType = 'number';
                } else if (colType.includes('date') || colType.includes('time')) {
                    fieldType = 'date';
                } else if (colName.includes('date') || colName.includes('time') || colName.includes('created') || colName.includes('modified')) {
                    fieldType = 'date';
                }

                // Show all fields by default - let users configure what to hide
                let defaultVisible = true;

                return {
                    field: col.name,
                    label: col.label || col.name,
                    visible: defaultVisible,
                    type: fieldType,
                    required: false, // API doesn't provide required info
                    maxLength: null,
                    columnType: col.type,
                    order: col.order
                };
            })
            .sort((a, b) => {
                // First sort by order if available, then by importance
                if (a.order && b.order) {
                    return a.order - b.order;
                }
                
                // Sort by importance: most useful fields first
                const getImportance = (field) => {
                    const fieldLower = field.toLowerCase();
                    
                    // Primary fields (most important)
                    if (fieldLower.includes('name') && !fieldLower.includes('filename')) return 1;
                    if (fieldLower.includes('type')) return 2;
                    if (fieldLower.includes('description')) return 3;
                    
                    // Secondary fields (important for BHA/Components)
                    if (fieldLower.includes('manufacturer')) return 10;
                    if (fieldLower.includes('serial')) return 11;
                    if (fieldLower.includes('length')) return 12;
                    if (fieldLower.includes('weight')) return 13;
                    if (fieldLower.includes('position')) return 14;
                    if (fieldLower.includes('depth')) return 15;
                    if (fieldLower.includes('hole') && fieldLower.includes('size')) return 16;
                    
                    // Technical fields
                    if (fieldLower.includes('size')) return 20;
                    if (fieldLower.includes('diameter')) return 21;
                    if (fieldLower.includes('material')) return 22;
                    if (fieldLower.includes('grade')) return 23;
                    
                    // ID fields (important but less visible)
                    if (fieldLower.includes('wellid')) return 90;
                    if (fieldLower.includes('bhaid')) return 91;
                    if (fieldLower.includes('componentid')) return 92;
                    if (fieldLower.includes('id')) return 95;
                    
                    // Audit fields (least important)
                    if (fieldLower.includes('created')) return 100;
                    if (fieldLower.includes('modified')) return 101;
                    if (fieldLower.includes('timestamp')) return 102;
                    
                    // Default fields
                    return 50;
                };
                
                return getImportance(a.field) - getImportance(b.field);
            });
        
        return result;
    }, []);

    // Helper function to merge saved display config with current column config
    const mergeDisplayConfigs = useCallback((savedConfig, defaultConfig) => {
        if (!Array.isArray(savedConfig) || !Array.isArray(defaultConfig)) {
            return defaultConfig;
        }

        // Create a map of saved configurations by field name
        const savedConfigMap = new Map();
        savedConfig.forEach(config => {
            if (config.field) {
                savedConfigMap.set(config.field, config);
            }
        });

        // Merge with default config, preserving user preferences where possible
        return defaultConfig.map(defaultItem => {
            const savedItem = savedConfigMap.get(defaultItem.field);
            if (savedItem) {
                // Merge saved preferences with current column metadata
                return {
                    ...defaultItem, // Keep current column metadata (type, required, etc.)
                    visible: savedItem.visible, // Preserve user's visibility preference
                    label: savedItem.label || defaultItem.label // Preserve custom labels if any
                };
            }
            return defaultItem;
        });
    }, []);

    // Load BHA data when well is selected
    const loadBhaData = useCallback(async (wellId) => {
        if (!wellId) {
            setBhaData([]);
            return;
        }

        try {
            setIsLoading(true);
            const bhaResponse = await apiFetch(`/api/db/BHA?filter=WellID,eq,${wellId}`);
            setBhaData(Array.isArray(bhaResponse) ? bhaResponse : []);
            
            // Clear selected BHA and components when well changes
            setSelectedBHA(null);
            setBhaComponentData([]);
            setBhaImage(null);
        } catch (err) {
            console.error('Failed to load BHA data:', err);
            setToast('❌ Failed to load BHA data');
        } finally {
            setIsLoading(false);
        }
    }, []);

    // Calculate BHA summary and statistics whenever data changes
    useEffect(() => {
        // Calculate BHA summary for selected BHA
        if (bhaComponentData && bhaComponentData.length > 0) {
            const summary = bhaComponentData.reduce((acc, component) => {
                const length = parseFloat(component.Length) || 0;
                const weight = parseFloat(component.Weight) || 0;
                const od = parseFloat(component.OD) || 0;

                acc.totalLength += length;
                acc.totalWeight += weight;
                if (od > acc.maxOD) {
                    acc.maxOD = od;
                }
                return acc;
            }, { totalLength: 0, totalWeight: 0, maxOD: 0, totalComponents: bhaComponentData.length });

            setBhaSummary(summary);
        } else {
            setBhaSummary({ totalLength: 0, totalWeight: 0, maxOD: 0, totalComponents: 0 });
        }

        // Calculate overall statistics for all BHA data
        const stats = {
            totalBHA: bhaData.length,
            totalComponents: bhaData.reduce((total, bha) => {
                // This is a simplified calculation - in a real app you'd load all components
                return total + (bha.ComponentCount || 0);
            }, 0),
            maxLength: bhaData.reduce((max, bha) => {
                const length = parseFloat(bha.TotalLength) || 0;
                return length > max ? length : max;
            }, 0),
            maxWeight: bhaData.reduce((max, bha) => {
                const weight = parseFloat(bha.TotalWeight) || 0;
                return weight > max ? weight : max;
            }, 0),
            maxOD: bhaData.reduce((max, bha) => {
                const od = parseFloat(bha.MaxOD) || 0;
                return od > max ? od : max;
            }, 0)
        };
        setStatistics(stats);
    }, [bhaComponentData, bhaData]);



    // Debug bhaImage state changes
    useEffect(() => {
        console.log('🖼️ BHA Image state changed:', bhaImage);
    }, [bhaImage]);

    // Image upload functions
    const uploadImageAsAttachment = useCallback(async (file) => {
        console.log('🖼️ Upload attempt started:', {
            file: file?.name,
            fileType: file?.type,
            fileSize: file?.size,
            selectedBHA: selectedBHA?.BHAID,
            selectedWellId
        });

        if (!selectedBHA || !selectedWellId) {
            console.warn('❌ Missing BHA or Well selection');
            setToast('❌ Please select a BHA first');
            return;
        }

        if (!file || !file.type.startsWith('image/')) {
            console.warn('❌ Invalid file type:', file?.type);
            setToast('❌ Only image files are allowed');
            return;
        }

        setIsUploading(true);
        try {
            console.log('📤 Creating FormData for upload...');
            const formData = new FormData();
            formData.append('file', file);
            formData.append('wellId', selectedWellId);
            formData.append('subject', `BHA Image - ${selectedBHA.BHAName || 'Unnamed'}`);
            formData.append('description', `BHA image for ${selectedBHA.BHAName || 'Unnamed BHA'} (ID: ${selectedBHA.BHAID}) - Category: BHA`);

            console.log('📡 Sending upload request to /api/attachments...');
            const response = await apiFetch('/api/attachments', {
                method: 'POST',
                body: formData
            });

            console.log('📥 Upload response received:', response);

            if (response && response.attachment) {
                const imageData = {
                    fileName: response.attachment.FileName,
                    fileSize: response.attachment.FileSize,
                    fileUrl: `/uploads/${response.attachment.FileName}`,
                    AttachmentID: response.attachment.AttachmentID
                };
                console.log('✅ Setting BHA image data:', imageData);
                setBhaImage(imageData);
                setToast('✅ Image uploaded successfully');
            } else {
                console.error('❌ Invalid response structure:', response);
                throw new Error('Failed to upload image - invalid response');
            }
        } catch (error) {
            console.error('❌ Image upload failed:', error);
            setToast('❌ Failed to upload image: ' + error.message);
        } finally {
            setIsUploading(false);
        }
    }, [selectedBHA, selectedWellId]);

    // Handle drag and drop events
    const handleDragOver = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(true);
    }, []);

    const handleDragLeave = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);
    }, []);

    const handleDrop = useCallback((e) => {
        console.log('🎯 Drop event triggered');
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);

        const files = Array.from(e.dataTransfer.files);
        console.log('📁 Files dropped:', files.length, files);
        
        if (files.length > 0) {
            const file = files[0];
            console.log('📄 Processing first file:', file.name, file.type);
            uploadImageAsAttachment(file);
        } else {
            console.warn('❌ No files found in drop event');
        }
    }, [uploadImageAsAttachment]);

    // Handle paste events
    const handlePaste = useCallback((e) => {
        console.log('📋 Paste event triggered');
        const items = Array.from(e.clipboardData.items);
        console.log('📋 Clipboard items:', items.length, items.map(item => item.type));
        
        const imageItem = items.find(item => item.type.startsWith('image/'));
        console.log('🖼️ Image item found:', imageItem?.type);
        
        if (imageItem) {
            e.preventDefault();
            const file = imageItem.getAsFile();
            console.log('📄 File from paste:', file?.name, file?.type, file?.size);
            if (file) {
                uploadImageAsAttachment(file);
            } else {
                console.warn('❌ Failed to get file from clipboard item');
            }
        } else {
            console.log('ℹ️ No image found in clipboard');
        }
    }, [uploadImageAsAttachment]);

    // Handle file input change
    const handleFileSelect = useCallback((e) => {
        console.log('📁 File input changed');
        const file = e.target.files[0];
        console.log('📄 Selected file:', file?.name, file?.type, file?.size);
        
        if (file) {
            uploadImageAsAttachment(file);
        } else {
            console.warn('❌ No file selected');
        }
        // Reset input
        e.target.value = '';
    }, [uploadImageAsAttachment]);



    // Format field values based on type
    const formatBhaFieldValue = (value, type) => {
        if (value === null || value === undefined || value === '') return '-';
        
        switch (type) {
            case 'number':
                return typeof value === 'number' ? value.toLocaleString() : value;
            case 'date':
                return new Date(value).toLocaleDateString();
            default:
                return value;
        }
    };

    // Render inline edit field for components
    const renderInlineEditField = (fieldName, value, type) => {
        const fieldValue = value || '';
        
        switch (type) {
            case 'number':
                return (
                    <input
                        type="number"
                        value={fieldValue}
                        onChange={(e) => handleComponentFieldChange(fieldName, e.target.value)}
                        className="inline-edit-input numeric"
                        step="any"
                    />
                );
            case 'date':
                return (
                    <input
                        type="date"
                        value={fieldValue ? fieldValue.split('T')[0] : ''}
                        onChange={(e) => handleComponentFieldChange(fieldName, e.target.value)}
                        className="inline-edit-input"
                    />
                );
            default:
                return (
                    <input
                        type="text"
                        value={fieldValue}
                        onChange={(e) => handleComponentFieldChange(fieldName, e.target.value)}
                        className="inline-edit-input"
                    />
                );
        }
    };

    // Load initial data
    useEffect(() => {
        const loadInitialData = async () => {
            try {
                setIsLoading(true);
                const allWellsResponse = await apiFetch('/api/wells');

                
                const allConfigs = await apiFetch('/api/config/columns');
                
                // Find BHA and BHA_Component configurations
                const bhaConfig = Array.isArray(allConfigs) ? allConfigs.find(table => table.tableName === 'BHA') : null;
                const componentConfig = Array.isArray(allConfigs) ? allConfigs.find(table => table.tableName === 'BHA_Component') : null;

                // Get all wells first
                const allWells = Array.isArray(allWellsResponse) ? allWellsResponse : [];
                setWells(allWells);

                setBhaColumns(bhaConfig?.columns || []);
                setComponentColumns(componentConfig?.columns || []);
                
                // Generate display configurations from column configurations
                const defaultBhaDisplayConfig = convertColumnConfigToDisplayConfig(bhaConfig, 'BHA');
                const defaultComponentDisplayConfig = convertColumnConfigToDisplayConfig(componentConfig, 'BHA_Component');
                
                // Load saved display configurations from localStorage, or use generated defaults
                const savedBhaConfig = localStorage.getItem('bhaDisplayConfig');
                const savedComponentConfig = localStorage.getItem('componentDisplayConfig');
                
                if (savedBhaConfig) {
                    try {
                        const parsed = JSON.parse(savedBhaConfig);
                        // Merge saved config with current column config to handle new/removed columns
                        const mergedBhaConfig = mergeDisplayConfigs(parsed, defaultBhaDisplayConfig);
                        setBhaDisplayConfig(mergedBhaConfig);
                    } catch (err) {
                        console.warn('Failed to parse saved BHA config, using default:', err);
                        setBhaDisplayConfig(defaultBhaDisplayConfig);
                    }
                } else {
                    setBhaDisplayConfig(defaultBhaDisplayConfig);
                }
                
                if (savedComponentConfig) {
                    try {
                        const parsed = JSON.parse(savedComponentConfig);
                        // Merge saved config with current column config to handle new/removed columns
                        const mergedComponentConfig = mergeDisplayConfigs(parsed, defaultComponentDisplayConfig);
                        setComponentDisplayConfig(mergedComponentConfig);
                    } catch (err) {
                        console.warn('Failed to parse saved component config, using default:', err);
                        setComponentDisplayConfig(defaultComponentDisplayConfig);
                    }
                } else {
                    setComponentDisplayConfig(defaultComponentDisplayConfig);
                }
                

            } catch (err) {
                console.error('Failed to load initial data:', err);
                setToast('❌ Failed to load initial data: ' + err.message);
                
                // Set empty configs to prevent render issues
                setBhaDisplayConfig([]);
                setComponentDisplayConfig([]);
            } finally {
                setIsLoading(false);
            }
        };

        loadInitialData();
    }, []); // Remove dependencies to prevent infinite loops

    // Load BHA components when BHA is selected
    const loadBhaComponents = useCallback(async (bhaId) => {
        if (!bhaId) {
            setBhaComponentData([]);
            return;
        }

        try {
            const response = await apiFetch(`/api/db/BHA_Component?filter=BHAID,eq,${bhaId}`);
            setBhaComponentData(Array.isArray(response) ? response : []);
        } catch (err) {
            console.error('Failed to load BHA components:', err);
            setToast('❌ Failed to load BHA components');
        }
    }, []);

    // Load BHA image/attachment
    const loadBhaImage = useCallback(async (bhaId) => {
        console.log('🔍 Loading BHA image for:', { bhaId, selectedWellId });
        
        if (!bhaId || !selectedWellId) {
            console.log('❌ Missing BHA ID or Well ID');
            setBhaImage(null);
            return;
        }

        try {
            // Get all attachments for this well
            console.log('📡 Fetching attachments from /api/attachments...');
            const response = await apiFetch('/api/attachments');
            console.log('📥 All attachments received:', response);
            
            const bhaAttachments = response.filter(att => 
                att.WellID === parseInt(selectedWellId) && 
                att.Subject?.includes(`BHA Image`) &&
                att.Description?.includes(`ID: ${bhaId}`) &&
                att.ContentType === 'image'
            );
            
            console.log('🔍 Filtered BHA attachments:', bhaAttachments);

            if (bhaAttachments.length > 0) {
                const attachment = bhaAttachments[0]; // Get the first/latest image
                const imageData = {
                    fileName: attachment.FileName,
                    fileSize: attachment.FileSize,
                    fileUrl: `/uploads/${attachment.FileName}`,
                    AttachmentID: attachment.AttachmentID
                };
                console.log('✅ Setting BHA image from attachment:', imageData);
                setBhaImage(imageData);
            } else {
                console.log('❌ No BHA attachments found');
                setBhaImage(null);
            }
        } catch (err) {
            console.error('❌ Failed to load BHA image:', err);
            setBhaImage(null);
        }
    }, [selectedWellId]);

    // Add keyboard escape handler for full screen mode
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape' && showFullScreenComponents) {
                setShowFullScreenComponents(false);
            }
        };

        if (showFullScreenComponents) {
            document.addEventListener('keydown', handleKeyDown);
            return () => document.removeEventListener('keydown', handleKeyDown);
        }
    }, [showFullScreenComponents]);

    // Add global paste event listener
    useEffect(() => {
        const handleGlobalPaste = (e) => {
            console.log('🌐 Global paste event detected');
            // Only handle paste if image section is focused or no other input is focused
            const activeElement = document.activeElement;
            const isInputFocused = activeElement && (
                activeElement.tagName === 'INPUT' || 
                activeElement.tagName === 'TEXTAREA' || 
                activeElement.contentEditable === 'true'
            );
            
            console.log('🎯 Active element:', activeElement?.tagName, 'Input focused:', isInputFocused, 'Selected BHA:', selectedBHA?.BHAID);
            
            if (!isInputFocused && selectedBHA) {
                console.log('✅ Handling global paste');
                handlePaste(e);
            } else {
                console.log('❌ Ignoring paste - input focused or no BHA selected');
            }
        };

        document.addEventListener('paste', handleGlobalPaste);
        return () => document.removeEventListener('paste', handleGlobalPaste);
    }, [selectedBHA, handlePaste]);

    // Handle well selection
    const handleWellChange = useCallback((e) => {
        const wellId = e.target.value;
        setSelectedWellId(wellId);
        loadBhaData(wellId);
    }, [loadBhaData]);

    // Handle BHA selection
    const handleBhaSelect = useCallback((bha) => {
        setSelectedBHA(bha);
        loadBhaComponents(bha.BHAID);
        loadBhaImage(bha.BHAID);
    }, [loadBhaComponents, loadBhaImage]);

    // Context menu handlers
    const handleContextMenu = useCallback((e, type, record) => {
        e.preventDefault();
        
        if (role === 'admin' || role === 'read-write') {
            setContextMenu({
                visible: true,
                x: e.clientX,
                y: e.clientY,
                type,
                record
            });
        }
    }, [role]);

    const handleCloseContextMenu = useCallback(() => {
        setContextMenu({ visible: false, x: 0, y: 0, type: '', record: null });
    }, []);

    // CRUD operations for BHA
    const handleAddBha = useCallback(() => {
        if (!selectedWellId) {
            setToast('Please select a well first');
            return;
        }
        console.log('➕ Opening BHA add form for well ID:', selectedWellId);
        console.log('Default parent data will be:', { WellID: selectedWellId });
        setEditingBhaId(null); // null for new record
        setShowBhaForm(true);
        handleCloseContextMenu();
    }, [selectedWellId, handleCloseContextMenu]);

    const handleEditBha = useCallback((bha) => {
        console.log('📝 Opening BHA edit form for:', bha);
        console.log('BHA ID:', bha.BHAID);
        console.log('Selected Well ID:', selectedWellId);
        setEditingBhaId(bha.BHAID);
        setShowBhaForm(true);
        handleCloseContextMenu();
    }, [handleCloseContextMenu, selectedWellId]);

    const handleDeleteBha = useCallback(async (bha) => {
        if (!window.confirm(`Are you sure you want to delete BHA "${bha.BHAName || 'this BHA'}"?\n\n⚠️ Deleting a BHA will also delete all its associated components.`)) {
            return;
        }

        try {
            await apiFetch(`/api/db/BHA/${bha.BHAID}`, { method: 'DELETE' });
            setToast('✅ BHA deleted successfully');
            loadBhaData(selectedWellId);
            
            // Clear selection if deleted BHA was selected
            if (selectedBHA && selectedBHA.BHAID === bha.BHAID) {
                setSelectedBHA(null);
                setBhaComponentData([]);
                setBhaImage(null);
            }
        } catch (err) {
            console.error('Failed to delete BHA:', err);
            
            // Check if it's a foreign key constraint error
            // The backend sends a structured error response, so we need to check the error details
            const isForeignKeyError = err.message && (
                err.message.includes('REFERENCE constraint') || 
                err.message.includes('FK_') || 
                err.message.includes('Foreign Key Constraint') ||
                err.message.includes('Cannot delete this record because it is referenced')
            );
            
            if (isForeignKeyError) {
                const cascadeConfirm = window.confirm(
                    `This BHA has associated components that must be deleted first.\n\n` +
                    `Would you like to delete this BHA and all its associated components?\n\n` +
                    `⚠️ This action cannot be undone.`
                );
                
                if (cascadeConfirm) {
                    try {
                        await apiFetch(`/api/db/BHA/${bha.BHAID}?cascade=true`, { method: 'DELETE' });
                        setToast('✅ BHA and all components deleted successfully');
                        loadBhaData(selectedWellId);
                        
                        // Clear selection if deleted BHA was selected
                        if (selectedBHA && selectedBHA.BHAID === bha.BHAID) {
                            setSelectedBHA(null);
                            setBhaComponentData([]);
                            setBhaImage(null);
                        }
                    } catch (cascadeErr) {
                        console.error('Failed to cascade delete BHA:', cascadeErr);
                        setToast(`❌ Failed to delete BHA and components: ${cascadeErr.message}`);
                    }
                }
            } else {
                setToast('❌ Failed to delete BHA');
            }
        }
        handleCloseContextMenu();
    }, [selectedWellId, selectedBHA, loadBhaData, handleCloseContextMenu]);

    // CRUD operations for BHA Components
    const handleEditComponentInline = useCallback((component) => {
        console.log('📝 Starting inline edit for component:', component);
        setEditingComponent({ ...component });
        handleCloseContextMenu();
    }, [handleCloseContextMenu]);

    const handleEditComponentFull = useCallback((component) => {
        console.log('📝 Opening full form edit for component:', component);
                        // Open DataForm with just the selected BHA and focus on this component
        setEditingBhaId(selectedBHA.BHAID);
        setShowBhaForm(true);
        handleCloseContextMenu();
    }, [selectedBHA, handleCloseContextMenu]);

    const handleDeleteComponent = useCallback(async (component) => {
        if (!window.confirm(`Are you sure you want to delete component "${component.ComponentName || component.Description || 'this component'}"?`)) {
            return;
        }

        try {
            console.log('🗑️ Attempting to delete component:', {
                ComponentID: component.ComponentID,
                ComponentName: component.ComponentName,
                Description: component.Description,
                selectedBHA: selectedBHA?.BHAID
            });
            
            const response = await apiFetch(`/api/db/BHA_Component/${component.ComponentID}`, { method: 'DELETE' });
            console.log('🗑️ Delete response:', response);
            
            // Check if the response indicates success (either success: true or deletedRow exists)
            if (response && (response.success || response.deletedRow)) {
                setToast('✅ Component deleted successfully');
                // Reload components for the current BHA
                await loadBhaComponents(selectedBHA.BHAID);
            } else {
                console.warn('⚠️ Delete response indicates failure:', response);
                throw new Error('Delete operation returned unsuccessful response');
            }
        } catch (err) {
            console.error('❌ Failed to delete component:', err);
            setToast(`❌ Failed to delete component: ${err.message}`);
        }
        handleCloseContextMenu();
    }, [selectedBHA, loadBhaComponents, handleCloseContextMenu]);

    // Double-click handler for inline editing
    const handleComponentDoubleClick = useCallback((component) => {
        if (role === 'admin' || role === 'read-write') {
            console.log('🖱️ Double-clicked component for inline edit:', component);
            setEditingComponent({ ...component });
        }
    }, [role]);

    // Keyboard handler for quick editing (Enter key)
    const handleComponentKeyDown = useCallback((e, component) => {
        if (e.key === 'Enter' && (role === 'admin' || role === 'read-write')) {
            e.preventDefault();
            console.log('⌨️ Enter key pressed for component edit:', component);
            setEditingComponent({ ...component });
        }
    }, [role]);

    const handleSaveInlineComponent = useCallback(async () => {
        if (!editingComponent) return;

        try {
            const componentId = editingComponent.ComponentID; // Store the ID before cleaning
            const cleanedComponent = { ...editingComponent };
            delete cleanedComponent.ComponentID; // Remove identity column for update
            
            await apiFetch(`/api/db/BHA_Component/${componentId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(cleanedComponent)
            });
            
            setToast('✅ Component updated successfully');
            setEditingComponent(null);
            // Reload components for the current BHA
            loadBhaComponents(selectedBHA.BHAID);
        } catch (err) {
            console.error('Failed to update component:', err);
            setToast('❌ Failed to update component: ' + err.message);
        }
    }, [editingComponent, selectedBHA, loadBhaComponents]);

    const handleCancelInlineEdit = useCallback(() => {
        setEditingComponent(null);
    }, []);

    const handleComponentFieldChange = useCallback((fieldName, value) => {
        if (!editingComponent) return;
        
        setEditingComponent(prev => ({
            ...prev,
            [fieldName]: value
        }));
    }, [editingComponent]);





    // Form save handlers
    const handleBhaSave = useCallback(async (result) => {
        console.log('🔥 DEBUG: handleBhaSave called with result:', result);
        console.log('🔥 DEBUG: Call stack:', new Error().stack);
        
        try {
            const { parentId } = result;
            console.log('✅ BHA save completed, result:', result);
            setToast('✅ BHA and components saved successfully');
            setShowBhaForm(false);
            setEditingBhaId(null);
            
            // Reload BHA data for the selected well
            await loadBhaData(selectedWellId);
            
            // If we just created a new BHA, select it
            if (!editingBhaId && parentId) {
                const updatedBhaData = await apiFetch(`/api/db/BHA?filter=WellID,eq,${selectedWellId}`);
                const newBha = Array.isArray(updatedBhaData) ? updatedBhaData.find(bha => bha.BHAID === parentId) : null;
                if (newBha) {
                    handleBhaSelect(newBha);
                }
            }
        } catch (err) {
            console.error('❌ Failed to save BHA:', err);
            setToast('❌ Failed to save BHA: ' + err.message);
        }
    }, [selectedWellId, editingBhaId, loadBhaData, handleBhaSelect]);

    // Context menu options
    const contextMenuOptions = useMemo(() => {
        const { type, record } = contextMenu;
        
        if (type === 'bha') {
            return [
                {
                    type: 'header',
                    label: 'BHA Actions',
                    icon: '🔧'
                },
                {
                    type: 'divider'
                },
                {
                    type: 'action',
                    label: 'Edit BHA & Components',
                    icon: '✏️',
                    onClick: () => handleEditBha(record)
                },
                {
                    type: 'action',
                    label: 'Delete BHA (with Components)',
                    icon: '🗑️',
                    onClick: () => handleDeleteBha(record)
                }
            ];
        }
        
        if (type === 'component') {
            return [
                {
                    type: 'header',
                    label: 'Component Actions',
                    icon: '⚙️'
                },
                {
                    type: 'divider'
                },
                {
                    type: 'action',
                    label: 'Quick Edit (Inline)',
                    icon: '✏️',
                    onClick: () => handleEditComponentInline(record)
                },
                {
                    type: 'action',
                    label: 'Full Edit (Form)',
                    icon: '📝',
                    onClick: () => handleEditComponentFull(record)
                },
                {
                    type: 'divider'
                },
                {
                    type: 'action',
                    label: 'Delete Component',
                    icon: '🗑️',
                    onClick: () => handleDeleteComponent(record)
                }
            ];
        }
        
        return [];
    }, [contextMenu, handleEditBha, handleDeleteBha, handleEditComponentInline, handleEditComponentFull, handleDeleteComponent]);

    // Toast management
    const clearToast = useCallback(() => setToast(''), []);

    useEffect(() => {
        if (toast) {
            const timer = setTimeout(clearToast, 2000);
            return () => clearTimeout(timer);
        }
    }, [toast, clearToast]);

    // Get selected well name for display
    const selectedWell = wells.find(w => w.WellID == selectedWellId);
    const selectedWellName = selectedWell ? selectedWell.WellName || selectedWell.Name || selectedWell.name : '';

    // Enhanced well selector
    const renderWellSelector = () => (
        <div className="well-selector-section">
            <div className="selector-row">
                <div className="selector-group">
                    <label htmlFor="well-select">Well:</label>
                    <select
                        id="well-select"
                        value={selectedWellId}
                        onChange={handleWellChange}
                        className="form-select"
                        disabled={isLoading}
                    >
                        <option value="">Select a well...</option>
                        {wells.map(well => (
                            <option key={well.WellID} value={well.WellID}>
                                {well.WellName || well.Name || well.name || `Well ${well.WellID}`}
                            </option>
                        ))}
                    </select>
                </div>
            </div>
        </div>
    );

    // Combined selector row with both well and BHA selectors
    const renderCombinedSelectors = () => (
        <div className="combined-selectors-section">
            <div className="selectors-row">
                <div className="selector-group">
                    <label htmlFor="well-select">Well:</label>
                    <select
                        id="well-select"
                        value={selectedWellId}
                        onChange={handleWellChange}
                        className="form-select"
                        disabled={isLoading}
                    >
                        <option value="">Select a well...</option>
                        {wells.map(well => (
                            <option key={well.WellID} value={well.WellID}>
                                {well.WellName || well.Name || well.name || `Well ${well.WellID}`}
                            </option>
                        ))}
                    </select>
                </div>
                
                <div className="selector-group">
                    <label htmlFor="bha-select">BHA:</label>
                    <select
                        id="bha-select"
                        value={selectedBHA?.BHAID || ''}
                        onChange={(e) => {
                            const bhaId = e.target.value;
                            if (bhaId) {
                                const bha = bhaData.find(b => b.BHAID == bhaId);
                                if (bha) {
                                    handleBhaSelect(bha);
                                }
                            } else {
                                setSelectedBHA(null);
                                setBhaComponentData([]);
                                setBhaImage(null);
                            }
                        }}
                        className="form-select"
                        disabled={!selectedWellId || bhaData.length === 0}
                    >
                        <option value="">Select a BHA...</option>
                        {bhaData.map(bha => {
                            const bhaName = bha.BHAName || `BHA #${bha.BHAID}`;
                            const description = bha.Description ? ` - ${bha.Description}` : '';
                            const totalLength = bha.TotalLength ? ` (${parseFloat(bha.TotalLength).toFixed(1)}m)` : '';
                            return (
                                <option key={bha.BHAID} value={bha.BHAID}>
                                    {bhaName}{description}{totalLength}
                                </option>
                            );
                        })}
                    </select>
                </div>
            </div>
        </div>
    );



    // Enhanced quick actions
    const renderQuickActions = () => (
        <div className="quick-actions">
            <button
                className="quick-action-btn small"
                onClick={handleAddBha}
                title="Add New BHA"
                disabled={!selectedWellId}
            >
                🔧 Add BHA
            </button>
            {selectedBHA && (role === 'admin' || role === 'read-write') && (
                <>
                    <button
                        className="quick-action-btn small"
                        onClick={() => handleEditBha(selectedBHA)}
                        title="Edit BHA & Components"
                    >
                        ✏️ Edit
                    </button>
                    <button
                        className="quick-action-btn small"
                        onClick={() => handleDeleteBha(selectedBHA)}
                        title="Delete BHA"
                    >
                        🗑️ Delete
                    </button>
                </>
            )}
            <button
                className="quick-action-btn small"
                onClick={() => setShowBhaConfig(true)}
                title="Configure Component Display"
            >
                ⚙️ Configure
            </button>
        </div>
    );
    
    // Enhanced BHA Summary with icons - compact version for header
    const renderBhaSummary = () => (
        <div className="bha-summary-compact">
            <div className="summary-items-horizontal">
                <div className="summary-item-compact">
                    <span className="summary-icon">📏</span>
                    <span className="summary-label">Length:</span>
                    <span className="summary-value">{bhaSummary.totalLength.toFixed(2)} m</span>
                </div>
                <div className="summary-item-compact">
                    <span className="summary-icon">⚖️</span>
                    <span className="summary-label">Weight:</span>
                    <span className="summary-value">{bhaSummary.totalWeight.toFixed(2)} kg</span>
                </div>
                <div className="summary-item-compact">
                    <span className="summary-icon">📐</span>
                    <span className="summary-label">Max OD:</span>
                    <span className="summary-value">{bhaSummary.maxOD.toFixed(3)} in</span>
                </div>
                <div className="summary-item-compact">
                    <span className="summary-icon">⚙️</span>
                    <span className="summary-label">Components:</span>
                    <span className="summary-value">{bhaSummary.totalComponents}</span>
                </div>
            </div>
        </div>
    );

    // Render BHA records table
    const renderBhaRecords = () => {
        if (!selectedWellId) {
            return (
                <div className="bha-records-placeholder">
                    <div className="placeholder-icon">🔧</div>
                    <p>Select a well to view BHA records</p>
                </div>
            );
        }

        if (bhaData.length === 0) {
            return (
                <div className="empty-state">
                    <div className="empty-icon">🔧</div>
                    <p>No BHA records found for this well</p>
                    {(role === 'admin' || role === 'read-write') && (
                        <button className="add-btn" onClick={handleAddBha}>
                            ➕ Add First BHA
                        </button>
                    )}
                </div>
            );
        }

        return (
            <div className="bha-records">
                <div className="section-header">
                    <div className="header-info">
                        <h3>🔧 BHA Records</h3>
                        <p>{bhaData.length} BHA records found</p>
                    </div>
                    <div className="header-actions">
                        {(role === 'admin' || role === 'read-write') && (
                            <span className="edit-hint">🖱️ Right-click for options</span>
                        )}
                    </div>
                </div>

                <div className="bha-records-table-container">
                    <table className="bha-records-table">
                        <thead>
                            <tr>
                                {bhaDisplayConfig.map(col => col.visible && (
                                    <th key={col.field}>{col.label}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {bhaData.map(bha => (
                                <tr 
                                    key={bha.BHAID}
                                    className={`bha-record-row ${selectedBHA && selectedBHA.BHAID === bha.BHAID ? 'selected' : ''}`}
                                    onContextMenu={(e) => handleContextMenu(e, 'bha', bha)}
                                    onClick={() => handleBhaSelect(bha)}
                                    title="Click to select BHA, right-click for options"
                                >
                                    {bhaDisplayConfig.map(col => col.visible && (
                                        <td key={col.field} className={`bha-record-cell ${col.type === 'number' ? 'numeric' : ''}`}>
                                            {formatBhaFieldValue(bha[col.field], col.type)}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    // Render BHA details for selected BHA
    const renderBhaDetails = () => {
        console.log('🔍 renderBhaDetails called, selectedBHA:', selectedBHA);
        
        if (!selectedBHA) {
            console.log('❌ No selectedBHA, returning null');
            return null;
        }

        console.log('✅ Rendering BHA details for:', selectedBHA.BHAName || selectedBHA.BHAID);

        // Define key BHA detail fields to display
        const bhaDetailFields = [
            { field: 'BHAName', label: 'Name', type: 'text' },
            { field: 'HoleSize', label: 'Hole Size', type: 'number', unit: 'in' },
            { field: 'DepthIn', label: 'Depth In', type: 'number', unit: 'm' },
            { field: 'DepthOut', label: 'Depth Out', type: 'number', unit: 'm' }
        ];

        return (
            <div className="bha-details">
                <div className="bha-details-single-row">
                    <div className="bha-details-title">
                        <span className="title-icon">📋</span>
                        <span className="title-text">BHA Section:</span>
                    </div>
                    {bhaDetailFields.map(field => {
                        const value = selectedBHA[field.field];
                        let displayValue = value;
                        
                        console.log(`📊 Field ${field.field}:`, value);
                        
                        // Handle different cases for empty values
                        if (value === null || value === undefined || value === '') {
                            displayValue = '-';
                        } else if (field.type === 'number' && !isNaN(value)) {
                            displayValue = `${parseFloat(value).toLocaleString()}${field.unit ? ' ' + field.unit : ''}`;
                        }
                        
                        return (
                            <div key={field.field} className="summary-item-bha">
                                <span className="summary-label">{field.label}:</span>
                                <span className="summary-value">{displayValue}</span>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    // Enhanced BHA components with icons
    const renderBhaComponents = () => {
        if (!selectedBHA) {
            return (
                <div className="components-placeholder">
                    <div className="placeholder-icon">⚙️</div>
                    <p>Select a BHA to view its components</p>
                </div>
            );
        }

        return (
            <div className="bha-components">
                <div className="section-header">
                    <div className="header-actions">
                        {(role === 'admin' || role === 'read-write') && (
                            <span className="edit-hint">🖱️ Double-click or Enter to edit</span>
                        )}
                        <button 
                            className="fullscreen-btn" 
                            onClick={() => setShowFullScreenComponents(true)}
                            title="View components in full screen"
                        >
                            🔍 Full Screen
                        </button>
                    </div>
                </div>

                {bhaComponentData.length === 0 ? (
                    <div className="empty-state">
                        <div className="empty-icon">⚙️</div>
                        <p>No components found for this BHA</p>
                        <p>Use "Edit BHA & Components" to add components</p>
                    </div>
                ) : (
                    <div className="components-table-container">
                        <table className="components-table">
                            <thead>
                                <tr>
                                    {componentDisplayConfig.map(col => col.visible && (
                                        <th key={col.field}>{col.label}</th>
                                    ))}
                                    {editingComponent && (
                                        <th className="inline-edit-actions">Actions</th>
                                    )}
                                </tr>
                            </thead>
                            <tbody>
                                {bhaComponentData.map(component => {
                                    const isEditing = editingComponent && editingComponent.ComponentID === component.ComponentID;
                                    
                                    return (
                                        <tr 
                                            key={component.ComponentID}
                                            className={`component-row ${isEditing ? 'editing' : ''}`}
                                            onContextMenu={(e) => handleContextMenu(e, 'component', component)}
                                            onDoubleClick={() => handleComponentDoubleClick(component)}
                                            onKeyDown={(e) => handleComponentKeyDown(e, component)}
                                            tabIndex={0}
                                            title={isEditing ? 'Editing mode - save or cancel changes' : 'Double-click to edit, Enter key to edit, right-click for options'}
                                        >
                                            {componentDisplayConfig.map(col => col.visible && (
                                                <td key={col.field} className={`component-cell ${col.type === 'number' ? 'numeric' : ''}`}>
                                                    {isEditing ? 
                                                        renderInlineEditField(col.field, editingComponent[col.field], col.type) :
                                                        formatBhaFieldValue(component[col.field], col.type)
                                                    }
                                                </td>
                                            ))}
                                            {editingComponent && (
                                                <td className="inline-edit-actions">
                                                    {isEditing ? (
                                                        <div className="edit-action-buttons">
                                                            <button 
                                                                className="save-inline-btn"
                                                                onClick={handleSaveInlineComponent}
                                                                title="Save changes"
                                                            >
                                                                ✅
                                                            </button>
                                                            <button 
                                                                className="cancel-inline-btn"
                                                                onClick={handleCancelInlineEdit}
                                                                title="Cancel changes"
                                                            >
                                                                ❌
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <span className="edit-placeholder">-</span>
                                                    )}
                                                </td>
                                            )}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        );
    };

    // Render BHA image
    const renderBhaImage = () => {
        if (!selectedBHA) {
            return (
                <div className="image-placeholder">
                    <p>Select a BHA to view images</p>
                </div>
            );
        }

        return (
            <div 
                className={`bha-image-section ${isDragOver ? 'drag-over' : ''}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onPaste={handlePaste}
                tabIndex={0} // Make div focusable for paste events
            >
                <div className="section-header">
                    <h3>BHA Image</h3>
                    <div className="image-controls">
                        {bhaImage && (
                            <button 
                                className="preview-btn"
                                onClick={() => setShowImagePreview(true)}
                                disabled={isUploading}
                            >
                                🔍 Preview
                            </button>
                        )}
                        <input
                            type="file"
                            id="image-file-input"
                            accept="image/*"
                            onChange={handleFileSelect}
                            style={{ display: 'none' }}
                        />
                        <button 
                            className="upload-btn"
                            onClick={() => document.getElementById('image-file-input').click()}
                            disabled={isUploading}
                        >
                            📁 Browse
                        </button>
                    </div>
                </div>

                {isUploading ? (
                    <div className="uploading-indicator">
                        <div className="spinner"></div>
                        <p>Uploading image...</p>
                    </div>
                ) : bhaImage ? (
                    <div className="bha-image-container">
                        <img 
                            src={bhaImage.fileUrl || `/uploads/${bhaImage.fileName}`}
                            alt={`BHA ${selectedBHA.BHAName || 'Image'}`}
                            className="bha-image"
                            onClick={() => setShowImagePreview(true)}
                            onLoad={() => console.log('✅ Image loaded successfully')}
                            onError={(e) => console.error('❌ Image failed to load:', e.target.src)}
                        />
                        <div className="image-info">
                            <p><strong>File:</strong> {bhaImage.fileName}</p>
                            <p><strong>Size:</strong> {bhaImage.fileSize ? `${(bhaImage.fileSize / 1024).toFixed(1)} KB` : 'Unknown'}</p>
                            <p><strong>Category:</strong> BHA</p>
                            <p><strong>Debug:</strong> {JSON.stringify(bhaImage)}</p>
                        </div>
                    </div>
                ) : (
                    <div className="no-image drop-zone">
                        <div className="no-image-icon">🖼️</div>
                        <p>No image available for this BHA</p>
                        <div className="drop-instructions">
                            <p><strong>Add an image:</strong></p>
                            <ul>
                                <li>📁 Click "Browse" to select a file</li>
                                <li>🖱️ Drag & drop an image here</li>
                                <li>📋 Copy & paste an image (Ctrl+V)</li>
                            </ul>
                        </div>
                    </div>
                )}

                {isDragOver && (
                    <div className="drag-overlay">
                        <div className="drag-message">
                            <div className="drag-icon">📤</div>
                            <p>Drop image here to upload</p>
                        </div>
                    </div>
                )}
            </div>
        );
    };



    return (
        <div className="wellbha-container fullscreen">
            {/* Header Controls */}
            <div className="wellbha-header">
                <div className="header-main">
                    <h1>🛠️ Well BHA</h1>
                    {renderCombinedSelectors()}
                </div>
                
                {renderQuickActions()}
            </div>



            {/* Main Content */}
            {isLoading ? (
                <div className="loading-indicator">
                    <div className="spinner"></div>
                    <p>Loading...</p>
                </div>
            ) : (
                <div className="wellbha-content-vertical">
                    {/* BHA Records Section */}
                    <div className="bha-records-section">
                        {renderBhaRecords()}
                    </div>

                    {/* BHA Details Section */}
                    {selectedBHA && (
                        <div className="bha-details-section">
                            {renderBhaDetails()}
                        </div>
                    )}

                    {/* BHA Components and Details */}
                    <div className="bha-components-section">
                        {selectedBHA ? (
                            <div className="bha-details-container">
                                <div className="details-header">
                                    <div className="header-info">
                                        <h2>⚙️ Components</h2>
                                        <p>{bhaComponentData.length} components loaded</p>
                                    </div>
                                    <div className="header-summary">
                                        {renderBhaSummary()}
                                    </div>
                                </div>
                                
                                <div className="tabs">
                                    <button 
                                        className={`tab-btn ${activeTab === 'components' ? 'active' : ''}`}
                                        onClick={() => setActiveTab('components')}>
                                        ⚙️ Components ({bhaComponentData.length})
                                    </button>
                                    <button 
                                        className={`tab-btn ${activeTab === 'image' ? 'active' : ''}`}
                                        onClick={() => setActiveTab('image')}>
                                        🖼️ Image
                                    </button>
                                </div>

                                <div className="tab-content">
                                    {activeTab === 'components' && renderBhaComponents()}
                                    {activeTab === 'image' && renderBhaImage()}
                                </div>
                            </div>
                        ) : (
                            <div className="placeholder-container">
                                <div className="placeholder-content">
                                    <div className="placeholder-icon">🔧</div>
                                    <h3>Select a BHA to View Components</h3>
                                    <p>Choose a BHA from the dropdown above or click on a BHA record to see its components and details.</p>
                                    {!selectedWellId && (
                                        <p><strong>First select a well from the dropdown at the top.</strong></p>
                                    )}
                                    {selectedWellId && bhaData.length === 0 && (
                                        <div className="no-data-actions">
                                            <p><strong>No BHA records found for this well.</strong></p>
                                            {(role === 'admin' || role === 'read-write') && (
                                                <button className="add-btn" onClick={handleAddBha}>
                                                    ➕ Add First BHA
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Modals */}
            {showBhaForm && (
                <div className="modal-overlay">
                    <div className="modal-content large-modal">
                        <DataForm
                            tables={{ parent: 'BHA', child: 'BHA_Component' }}
                            identityColumns={{ parent: 'BHAID', child: 'ComponentID' }}
                            matchKey={{ parentKey: 'BHAID', childForeignKey: 'BHAID' }}
                            recordId={editingBhaId}
                            defaultParentData={editingBhaId ? {} : { WellID: selectedWellId }}
                            hiddenParentFields={['WellID']}
                            columnsPerRow={4}
                            skipAutoSave={true}
                            onSave={handleBhaSave}
                            onCancel={() => {
                                console.log('🚫 DataForm cancelled');
                                setShowBhaForm(false);
                                setEditingBhaId(null);
                            }}
                            title={`BHA Form${selectedWellName ? ' - ' + selectedWellName : ''}`}
                            onDataExtracted={(extractionResult) => {
                                console.log('📊 BHA DataForm extraction result:', extractionResult);
                                // The DataForm will handle the data automatically
                                // This callback is just for logging and any additional processing
                            }}
                        />
                    </div>
                </div>
            )}

            {/* BHA Configuration Modal */}
            {showBhaConfig && (
                <div className="modal-overlay">
                    <div className="modal-content config-modal">
                        <div className="modal-header">
                            <h3>Configure Table Columns</h3>
                            <button 
                                className="close-btn"
                                onClick={() => setShowBhaConfig(false)}
                            >
                                ✕
                            </button>
                        </div>
                        
                        <div className="config-intro">
                            <p>
                                <strong>💡 How it works:</strong> All available database columns are shown by default. 
                                Use the checkboxes below to show/hide specific columns in the tables. 
                                Your preferences will be saved automatically.
                            </p>
                        </div>
                        
                        <div className="config-content">
                            <div className="config-section">
                                <h4>BHA Table Columns</h4>
                                <p className="config-description">
                                    All columns are shown by default. Check/uncheck to control which columns are displayed in the BHA table.
                                    {bhaDisplayConfig.length > 0 ? 
                                        ` Currently showing ${bhaDisplayConfig.filter(col => col.visible).length} of ${bhaDisplayConfig.length} columns.` : 
                                        ' No columns configured.'
                                    }
                                </p>
                                <div className="config-controls">
                                    <button 
                                        className="select-all-btn"
                                        onClick={() => {
                                            const newConfig = bhaDisplayConfig.map(col => ({ ...col, visible: true }));
                                            setBhaDisplayConfig(newConfig);
                                        }}
                                        type="button"
                                    >
                                        ✅ Select All
                                    </button>
                                    <button 
                                        className="deselect-all-btn"
                                        onClick={() => {
                                            const newConfig = bhaDisplayConfig.map(col => ({ ...col, visible: false }));
                                            setBhaDisplayConfig(newConfig);
                                        }}
                                        type="button"
                                    >
                                        ❌ Deselect All
                                    </button>
                                </div>
                                <div className="config-fields">
                                    {bhaDisplayConfig.map((col, index) => (
                                        <div key={col.field} className="config-field">
                                            <label>
                                                <input
                                                    type="checkbox"
                                                    checked={col.visible}
                                                    onChange={(e) => {
                                                        const newConfig = [...bhaDisplayConfig];
                                                        newConfig[index].visible = e.target.checked;
                                                        setBhaDisplayConfig(newConfig);
                                                    }}
                                                />
                                                <span className="field-label">{col.label}</span>
                                                <span className="field-type">({col.type})</span>
                                                {col.required && <span className="field-required">*</span>}
                                                {col.columnType && <span className="field-db-type">[{col.columnType}]</span>}
                                            </label>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="config-section">
                                <h4>Component Table Columns</h4>
                                <p className="config-description">
                                    All columns are shown by default. Check/uncheck to control which columns are displayed in the Component table.
                                    {componentDisplayConfig.length > 0 ? 
                                        ` Currently showing ${componentDisplayConfig.filter(col => col.visible).length} of ${componentDisplayConfig.length} columns.` : 
                                        ' No columns configured.'
                                    }
                                </p>
                                <div className="config-controls">
                                    <button 
                                        className="select-all-btn"
                                        onClick={() => {
                                            const newConfig = componentDisplayConfig.map(col => ({ ...col, visible: true }));
                                            setComponentDisplayConfig(newConfig);
                                        }}
                                        type="button"
                                    >
                                        ✅ Select All
                                    </button>
                                    <button 
                                        className="deselect-all-btn"
                                        onClick={() => {
                                            const newConfig = componentDisplayConfig.map(col => ({ ...col, visible: false }));
                                            setComponentDisplayConfig(newConfig);
                                        }}
                                        type="button"
                                    >
                                        ❌ Deselect All
                                    </button>
                                </div>
                                <div className="config-fields">
                                    {componentDisplayConfig.map((col, index) => (
                                        <div key={col.field} className="config-field">
                                            <label>
                                                <input
                                                    type="checkbox"
                                                    checked={col.visible}
                                                    onChange={(e) => {
                                                        const newConfig = [...componentDisplayConfig];
                                                        newConfig[index].visible = e.target.checked;
                                                        setComponentDisplayConfig(newConfig);
                                                    }}
                                                />
                                                <span className="field-label">{col.label}</span>
                                                <span className="field-type">({col.type})</span>
                                                {col.required && <span className="field-required">*</span>}
                                                {col.columnType && <span className="field-db-type">[{col.columnType}]</span>}
                                            </label>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className="modal-footer">
                            <button 
                                className="save-config-btn"
                                onClick={() => {
                                    // Save configuration to localStorage
                                    localStorage.setItem('bhaDisplayConfig', JSON.stringify(bhaDisplayConfig));
                                    localStorage.setItem('componentDisplayConfig', JSON.stringify(componentDisplayConfig));
                                    setToast('✅ Display configuration saved');
                                    setShowBhaConfig(false);
                                }}
                            >
                                💾 Save Configuration
                            </button>
                            <button 
                                className="reset-config-btn"
                                onClick={() => {
                                    // Reset to default configuration based on current column configurations
                                    const bhaConfig = bhaColumns.length > 0 ? { columns: bhaColumns } : null;
                                    const componentConfig = componentColumns.length > 0 ? { columns: componentColumns } : null;
                                    
                                    if (bhaConfig) {
                                        const defaultBhaConfig = convertColumnConfigToDisplayConfig(bhaConfig, 'BHA');
                                        setBhaDisplayConfig(defaultBhaConfig);
                                    }
                                    
                                    if (componentConfig) {
                                        const defaultComponentConfig = convertColumnConfigToDisplayConfig(componentConfig, 'BHA_Component');
                                        setComponentDisplayConfig(defaultComponentConfig);
                                    }
                                    
                                    // Clear saved configurations
                                    localStorage.removeItem('bhaDisplayConfig');
                                    localStorage.removeItem('componentDisplayConfig');
                                    
                                    setToast('🔄 Configuration reset to defaults from column configuration');
                                }}
                            >
                                🔄 Reset to Default
                            </button>
                            <button 
                                className="cancel-btn"
                                onClick={() => setShowBhaConfig(false)}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Image Preview Modal */}
            {showImagePreview && bhaImage && (
                <div className="modal-overlay image-preview-modal" onClick={() => setShowImagePreview(false)}>
                    <div className="image-preview-content" onClick={e => e.stopPropagation()}>
                        <div className="preview-header">
                            <h3>BHA Image Preview</h3>
                            <button 
                                className="close-btn"
                                onClick={() => setShowImagePreview(false)}
                            >
                                ✕
                            </button>
                        </div>
                        <div className="preview-image-container">
                            <img 
                                src={bhaImage.fileUrl || `/uploads/${bhaImage.fileName}`}
                                alt={`BHA ${selectedBHA.BHAName || 'Image'}`}
                                className="preview-image"
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* Full Screen Components Modal */}
            {showFullScreenComponents && selectedBHA && (
                <div className="modal-overlay fullscreen-modal">
                    <div className="fullscreen-content">
                        <div className="fullscreen-header">
                            <div className="fullscreen-title">
                                <h2>🔧 {selectedBHA.BHAName || 'BHA'} Components</h2>
                                <div className="fullscreen-summary">
                                    {renderBhaSummary()}
                                </div>
                            </div>
                            <div className="fullscreen-actions">
                                {(role === 'admin' || role === 'read-write') && (
                                    <span className="edit-hint">🖱️ Double-click or Enter to edit</span>
                                )}
                                <button 
                                    className="close-btn"
                                    onClick={() => setShowFullScreenComponents(false)}
                                    title="Exit full screen"
                                >
                                    ✕ Exit Full Screen
                                </button>
                            </div>
                        </div>
                        <div className="fullscreen-table-container">
                            {bhaComponentData.length === 0 ? (
                                <div className="empty-state">
                                    <div className="empty-icon">⚙️</div>
                                    <p>No components found for this BHA</p>
                                    <p>Use "Edit BHA & Components" to add components</p>
                                </div>
                            ) : (
                                <table className="components-table fullscreen-table">
                                    <thead>
                                        <tr>
                                            {componentDisplayConfig.map(col => col.visible && (
                                                <th key={col.field}>{col.label}</th>
                                            ))}
                                            {editingComponent && (
                                                <th className="inline-edit-actions">Actions</th>
                                            )}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {bhaComponentData.map(component => {
                                            const isEditing = editingComponent && editingComponent.ComponentID === component.ComponentID;
                                            
                                            return (
                                                <tr 
                                                    key={component.ComponentID}
                                                    className={`component-row ${isEditing ? 'editing' : ''}`}
                                                    onContextMenu={(e) => handleContextMenu(e, 'component', component)}
                                                    onDoubleClick={() => handleComponentDoubleClick(component)}
                                                    onKeyDown={(e) => handleComponentKeyDown(e, component)}
                                                    tabIndex={0}
                                                    title={isEditing ? 'Editing mode - save or cancel changes' : 'Double-click to edit, Enter key to edit, right-click for options'}
                                                >
                                                    {componentDisplayConfig.map(col => col.visible && (
                                                        <td key={col.field} className={`component-cell ${col.type === 'number' ? 'numeric' : ''}`}>
                                                            {isEditing ? 
                                                                renderInlineEditField(col.field, editingComponent[col.field], col.type) :
                                                                formatBhaFieldValue(component[col.field], col.type)
                                                            }
                                                        </td>
                                                    ))}
                                                    {editingComponent && (
                                                        <td className="inline-edit-actions">
                                                            {isEditing ? (
                                                                <div className="edit-action-buttons">
                                                                    <button 
                                                                        className="save-inline-btn"
                                                                        onClick={handleSaveInlineComponent}
                                                                        title="Save changes"
                                                                    >
                                                                        ✅
                                                                    </button>
                                                                    <button 
                                                                        className="cancel-inline-btn"
                                                                        onClick={handleCancelInlineEdit}
                                                                        title="Cancel changes"
                                                                    >
                                                                        ❌
                                                                    </button>
                                                                </div>
                                                            ) : (
                                                                <span className="edit-placeholder">-</span>
                                                            )}
                                                        </td>
                                                    )}
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Context Menu */}
            <ContextMenu
                x={contextMenu.x}
                y={contextMenu.y}
                isVisible={contextMenu.visible}
                onClose={handleCloseContextMenu}
                options={contextMenuOptions}
            />

            {/* Toast Notification */}
            {toast && (
                <div className="toast-notification">
                    {toast}
                </div>
            )}
        </div>
    );
};

export default WellBHA;